import type { UTMParameter, UTMParams } from './types';

/**
 * List of UTM parameters to track
 */
const UTM_PARAMS: readonly UTMParameter[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
] as const;

/**
 * Captures UTM parameters from the current URL and stores them in localStorage.
 * Only updates localStorage if a parameter is present in the URL.
 *
 * @returns void
 */
export function captureUTMParams(): void {
  if (typeof window === 'undefined') return;

  try {
    const searchParams = new URLSearchParams(window.location.search);

    UTM_PARAMS.forEach((param) => {
      const value = searchParams.get(param);
      if (value) {
        localStorage.setItem(param, value);
      }
    });
  } catch (error) {
    console.error('[UTM] Failed to capture UTM parameters:', error);
  }
}

/**
 * Retrieves all stored UTM parameters from localStorage.
 *
 * @returns UTMParams object with all UTM values (null if not found)
 */
export function getStoredUTMParams(): UTMParams {
  if (typeof window === 'undefined') {
    return {
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_content: null,
    };
  }

  try {
    return {
      utm_source: localStorage.getItem('utm_source'),
      utm_medium: localStorage.getItem('utm_medium'),
      utm_campaign: localStorage.getItem('utm_campaign'),
      utm_content: localStorage.getItem('utm_content'),
    };
  } catch (error) {
    console.error('[UTM] Failed to retrieve UTM parameters:', error);
    return {
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_content: null,
    };
  }
}

/**
 * Clears all stored UTM parameters from localStorage.
 * Useful for testing or privacy compliance.
 *
 * @returns void
 */
export function clearUTMParams(): void {
  if (typeof window === 'undefined') return;

  try {
    UTM_PARAMS.forEach((param) => {
      localStorage.removeItem(param);
    });
  } catch (error) {
    console.error('[UTM] Failed to clear UTM parameters:', error);
  }
}
